import { Injectable } from '@angular/core';
import { SupabaseService } from './base/supabase.service';

export interface KycFieldLock {
  locked: boolean;
  /** Valor atual do campo no cp_pii — só vem preenchido quando locked. */
  value: any;
}

const NOT_LOCKED: KycFieldLock = { locked: false, value: null };
const CACHE_TTL_MS = 30_000;

/**
 * CPK-5059: trava das perguntas com `configuration.fillInfo.lockWhenKycPassed`.
 *
 * Mesma regra do backend (processFillInfo): a pergunta trava quando o dono tem KYC aprovado
 * (stage DONE + passed) e o campo do fillInfo já está preenchido no cp_pii. Travada, ela mostra
 * o valor do cadastro e não deixa editar. Aqui é só a tela — quem garante é o backend.
 */
@Injectable({
  providedIn: 'root',
})
export class KycFieldLockService {
  private readonly cache = new Map<string, { at: number; value: Promise<any> }>();

  constructor(private sbs: SupabaseService) {}

  /** Campo do cp_pii protegido pela pergunta, ou null se ela não tem a flag. */
  static lockedField(question: { configuration?: any } | null | undefined): string | null {
    const fillInfo = question?.configuration?.fillInfo;
    if (fillInfo?.version !== 2 || fillInfo?.target !== 'profile' || fillInfo?.lockWhenKycPassed !== true) {
      return null;
    }
    return typeof fillInfo.field === 'string' && fillInfo.field ? fillInfo.field : null;
  }

  async resolve(question: { configuration?: any } | null | undefined, userId: string): Promise<KycFieldLock> {
    const field = KycFieldLockService.lockedField(question);
    if (!field || !userId) return NOT_LOCKED;

    const [column, child] = field.split('.');
    try {
      const [hasKyc, row] = await Promise.all([
        this.cached(`kyc:${userId}`, () => this.fetchHasApprovedKyc(userId)),
        this.cached(`pii:${userId}:${column}`, () => this.fetchPiiColumn(userId, column)),
      ]);
      const raw = row?.[column];
      const value = child ? raw?.[child] : raw;
      const filled = value !== null && value !== undefined && String(value).trim() !== '';
      return hasKyc && filled ? { locked: true, value } : NOT_LOCKED;
    } catch (error) {
      // Sem a consulta, a pergunta segue editável; o backend continua sem gravar no cadastro.
      console.error('[KycFieldLock] Error resolving lock for field', field, error);
      return NOT_LOCKED;
    }
  }

  private cached<T>(key: string, load: () => Promise<T>): Promise<T> {
    const hit = this.cache.get(key);
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.value;

    const value = load().catch((error) => {
      this.cache.delete(key);
      throw error;
    });
    this.cache.set(key, { at: Date.now(), value });
    return value;
  }

  private async fetchHasApprovedKyc(userId: string): Promise<boolean> {
    const { data, error } = await (this.sbs.supabase as any)
      .from('cp_kyc_check')
      .select('id')
      .eq('checked_user_id', userId)
      .eq('stage', 'DONE')
      .eq('passed', true)
      .limit(1);

    if (error) throw new Error(error.message);
    return (data ?? []).length > 0;
  }

  private async fetchPiiColumn(userId: string, column: string): Promise<any> {
    const { data, error } = await (this.sbs.supabase as any)
      .from('cp_pii')
      .select(column)
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw new Error(error.message);
    return data;
  }
}
